import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Megaphone, Pin, Pencil, Trash2, Clock, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = Math.floor((new Date() - d) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function AnnModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ title: '', body: '', priority: 'normal', pinned: false, department: 'all' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ title: editing.title || '', body: editing.body || '', priority: editing.priority || 'normal', pinned: editing.pinned || false, department: editing.department || 'all' });
    else setForm({ title: '', body: '', priority: 'normal', pinned: false, department: 'all' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/operations/announcements/${editing.id}`, form)
        : await axios.post(`${API}/api/operations/announcements`, form);
      toast.success(editing ? 'Updated' : 'Posted');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg"><DialogHeader><DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-amber-500" />{editing ? 'Edit' : 'New'} Announcement</DialogTitle><DialogDescription>Post updates to your team</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Announcement title" data-testid="ann-title" /></div>
          <div><Label>Body</Label><Textarea value={form.body} onChange={e => setForm(p => ({...p, body: e.target.value}))} placeholder="Write your announcement..." rows={4} data-testid="ann-body" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priority</Label><Select value={form.priority} onValueChange={v => setForm(p => ({...p, priority: v}))}><SelectTrigger data-testid="ann-priority"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Department</Label><Select value={form.department} onValueChange={v => setForm(p => ({...p, department: v}))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="sales">Sales</SelectItem><SelectItem value="marketing">Marketing</SelectItem><SelectItem value="support">Support</SelectItem><SelectItem value="engineering">Engineering</SelectItem></SelectContent></Select></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.pinned} onCheckedChange={v => setForm(p => ({...p, pinned: v}))} /><Label>Pin to top</Label></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-ann-btn">{saving ? 'Posting...' : editing ? 'Update' : 'Post'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

export default function AnnouncementsPage() {
  const [anns, setAnns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAnns = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/operations/announcements`); setAnns(data); }
    catch { toast.error('Failed to load announcements'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAnns(); }, [fetchAnns]);

  const handleSaved = (ann, isUpdate) => { if (isUpdate) setAnns(p => p.map(a => a.id === ann.id ? ann : a)); else setAnns(p => [ann, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete announcement?')) return; try { await axios.delete(`${API}/api/operations/announcements/${id}`); setAnns(p => p.filter(a => a.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const togglePin = async (ann) => {
    try { const { data } = await axios.put(`${API}/api/operations/announcements/${ann.id}`, { pinned: !ann.pinned }); setAnns(p => p.map(a => a.id === ann.id ? data : a).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0))); toast.success(ann.pinned ? 'Unpinned' : 'Pinned'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="announcements-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Megaphone className="h-5 w-5 text-amber-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Announcements<PageInfoTooltip pageKey="announcements" /></h1><p className="text-xs text-gray-400">{anns.length} announcements · {anns.filter(a => a.pinned).length} pinned</p></div></div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-amber-600 hover:bg-amber-700 h-9 gap-1.5" data-testid="new-ann-btn"><Plus className="h-4 w-4" /> New Announcement</Button>
      </div>

      {loading ? <div className="flex items-center justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div> : anns.length === 0 ? (
        <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No announcements</h3><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-amber-600 hover:bg-amber-700 gap-1.5 mt-3"><Plus className="h-4 w-4" /> Post Announcement</Button></CardContent></Card>
      ) : (
        <div className="space-y-3 max-w-3xl">
          {anns.map(ann => {
            const pri = PRIORITIES.find(p => p.value === ann.priority) || PRIORITIES[1];
            return (
              <Card key={ann.id} className={`border-gray-200 ${ann.pinned ? 'ring-2 ring-amber-200' : ''}`} data-testid={`ann-card-${ann.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    {ann.pinned && <Pin className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />}
                    {ann.priority === 'urgent' && <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-base font-bold text-gray-900">{ann.title}</h3>
                        <Badge className={`${pri.color} text-[9px]`}>{pri.label}</Badge>
                        {ann.department !== 'all' && <Badge variant="outline" className="text-[9px]">{ann.department}</Badge>}
                      </div>
                      {ann.body && <p className="text-sm text-gray-600 whitespace-pre-wrap mb-2">{ann.body}</p>}
                      <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <span>{ann.created_by_name || 'System'}</span>
                        <span className="flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" /> {timeAgo(ann.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => togglePin(ann)} data-testid={`pin-ann-${ann.id}`}><Pin className={`h-3.5 w-3.5 ${ann.pinned ? 'text-amber-500' : 'text-gray-400'}`} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(ann); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(ann.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <AnnModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />
    </div>
  );
}
